import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import api from '../lib/api';

/**
 * Response shape from GET /modules/tenant/:tenantId/features
 * 
 * Each key is a PlatformModule name (e.g., 'CORE_BANKING', 'CRYPTO')
 * Value is the feature config for that module, including _enabled flag.
 * 
 * @example
 * {
 *   CORE_BANKING: { _enabled: true, pixEnabled: true, tedEnabled: false, boletoEnabled: true },
 *   BENEFITS: { _enabled: true },
 *   CRYPTO: { _enabled: false },
 *   ...
 * }
 */
export interface TenantFeatureMap {
  [moduleId: string]: {
    _enabled: boolean;
    [featureKey: string]: any;
  };
}

/**
 * Hook to fetch and cache all tenant features (module configs).
 * Refreshes every 30 seconds and is cached/shared across components.
 * 
 * @example
 * const { isFeatureEnabled, features, isLoading } = useTenantFeatures();
 * 
 * // Check if PIX is enabled for the tenant
 * if (isFeatureEnabled('CORE_BANKING', 'pixEnabled')) {
 *   // Show PIX features
 * }
 * 
 * // Check if module is enabled
 * if (isFeatureEnabled('BENEFITS')) {
 *   // Show benefits section
 * }
 */
export function useTenantFeatures() {
  const { user } = useAuthStore();
  const tenantId = user?.tenantId;

  const query = useQuery<TenantFeatureMap>({
    queryKey: ['tenant-features', tenantId],
    queryFn: async () => {
      if (!tenantId) return {};
      const { data } = await api.get(`/modules/tenant/${tenantId}/features`);
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  /**
   * Check if a specific feature within a module is enabled.
   * If only moduleId is provided, checks if the module itself is enabled.
   * If featureKey is also provided, checks the granular feature flag.
   * 
   * @param moduleId - PlatformModule enum value (e.g., 'CORE_BANKING')
   * @param featureKey - Optional feature flag key (e.g., 'pixEnabled')
   * @returns true if enabled, false otherwise (defaults to false while loading)
   */
  const isFeatureEnabled = (moduleId: string, featureKey?: string): boolean => {
    const moduleConfig = query.data?.[moduleId];
    if (!moduleConfig) return false;
    if (!moduleConfig._enabled) return false;
    if (!featureKey) return true;
    
    // If key doesn't exist in config, default to true (backward compat)
    if (moduleConfig[featureKey] === undefined) return true;
    return !!moduleConfig[featureKey];
  };

  /**
   * Get all features for a specific module.
   * Returns undefined if module is not found or data hasn't loaded yet.
   */
  const getModuleFeatures = (moduleId: string) => {
    return query.data?.[moduleId];
  };

  return {
    features: query.data || {},
    isFeatureEnabled,
    getModuleFeatures,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
